import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '@/components/Layout';
import { API } from '@/App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Plus, Trash2, Edit, Save, GripVertical, 
  ToggleLeft, ToggleRight, Monitor, Palette
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from '@/components/ui/badge';
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const COLORES_PRESET = [
  { value: '#3B82F6', label: 'Azul' },
  { value: '#10B981', label: 'Verde' },
  { value: '#F59E0B', label: 'Amarillo' },
  { value: '#EF4444', label: 'Rojo' },
  { value: '#8B5CF6', label: 'Púrpura' },
  { value: '#EC4899', label: 'Rosa' },
  { value: '#06B6D4', label: 'Cyan' },
  { value: '#F97316', label: 'Naranja' },
];

const BoxConfigPage = () => {
  const [boxes, setBoxes] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [editingBox, setEditingBox] = useState(null);
  
  const [formData, setFormData] = useState({
    nombre: '',
    activo: true,
    empresa_id: '',
    color: '#3B82F6'
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [boxesRes, empresasRes] = await Promise.all([
        axios.get(`${API}/puntos-atencion`, { withCredentials: true }),
        axios.get(`${API}/empresas?active_only=true`, { withCredentials: true })
      ]);
      setBoxes(boxesRes.data);
      setEmpresas(empresasRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOpenDialog = (box = null) => {
    if (box) {
      setEditingBox(box);
      setFormData({
        nombre: box.nombre,
        activo: box.activo,
        empresa_id: box.empresa_id || '',
        color: box.color || '#3B82F6'
      });
    } else {
      setEditingBox(null);
      setFormData({
        nombre: '',
        activo: true,
        empresa_id: '',
        color: '#3B82F6'
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingBox(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nombre.trim()) {
      toast.error('El nombre es requerido');
      return;
    }

    const dataToSend = {
      ...formData,
      empresa_id: formData.empresa_id || null
    };

    try {
      if (editingBox) {
        await axios.put(`${API}/puntos-atencion/${editingBox.id}`, dataToSend, {
          withCredentials: true
        });
        toast.success('Box actualizado');
      } else {
        await axios.post(`${API}/puntos-atencion`, dataToSend, {
          withCredentials: true
        });
        toast.success('Box creado');
      }
      handleCloseDialog();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleToggleActive = async (box) => {
    try {
      await axios.put(`${API}/puntos-atencion/${box.id}`, {
        activo: !box.activo
      }, { withCredentials: true });
      toast.success(box.activo ? 'Box desactivado' : 'Box activado');
      fetchData();
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    
    try {
      await axios.delete(`${API}/puntos-atencion/${deleteId}`, {
        withCredentials: true
      });
      toast.success('Box eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar');
    } finally {
      setDeleteId(null);
    }
  };

  const moveBox = async (index, direction) => {
    const newBoxes = [...boxes];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (newIndex < 0 || newIndex >= newBoxes.length) return;
    
    [newBoxes[index], newBoxes[newIndex]] = [newBoxes[newIndex], newBoxes[index]];
    
    const reorderedItems = newBoxes.map((box, idx) => ({
      id: box.id,
      orden: idx
    }));
    
    try {
      await axios.post(`${API}/puntos-atencion/reorder`, reorderedItems, {
        withCredentials: true
      });
      fetchData();
    } catch (error) {
      toast.error('Error al reordenar');
    }
  };

  const getEmpresaNombre = (empresaId) => {
    if (!empresaId) return null;
    const empresa = empresas.find(e => e.id === empresaId);
    return empresa?.nombre || 'Empresa no encontrada';
  };

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="box-config-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 heading">Configuración de Boxes</h1>
            <p className="text-slate-500 mt-1">
              Configure los puntos de atención para el sistema de turnos
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => window.open('/display', '_blank')}
              className="gap-2"
            >
              <Monitor className="w-4 h-4" />
              Ver Pantalla
            </Button>
            <Button 
              className="btn-accent gap-2" 
              onClick={() => handleOpenDialog()}
              data-testid="add-box-btn"
            >
              <Plus className="w-4 h-4" />
              Agregar Box
            </Button>
          </div>
        </div>

        {/* Info Card */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <p className="text-blue-800">
              <strong>Información:</strong> Los boxes son los puntos de atención donde se llaman los turnos. 
              Puede crear boxes específicos para empresas con convenio o boxes generales como "Box 1", "Vacunación", etc.
            </p>
          </CardContent>
        </Card>

        {/* Boxes List */}
        <Card>
          <CardHeader>
            <CardTitle>
              Boxes Configurados ({boxes.filter(b => b.activo).length} activos de {boxes.length})
            </CardTitle>
            <CardDescription>
              Arrastre para reordenar. Los boxes activos aparecerán en la pantalla de turnos.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="loading-spinner w-8 h-8"></div>
              </div>
            ) : boxes.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Monitor className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <p className="text-lg mb-2">No hay boxes configurados</p>
                <Button onClick={() => handleOpenDialog()} className="gap-2 mt-4">
                  <Plus className="w-4 h-4" />
                  Agregar primer box
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {boxes.map((box, index) => (
                  <div 
                    key={box.id}
                    className={`flex items-center gap-4 p-4 border rounded-lg transition-all ${
                      box.activo ? 'bg-white' : 'bg-slate-50 opacity-60'
                    }`}
                  >
                    {/* Reorder buttons */}
                    <div className="flex flex-col gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => moveBox(index, 'up')}
                        disabled={index === 0}
                        className="h-6 w-6 p-0"
                      >
                        ▲
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => moveBox(index, 'down')}
                        disabled={index === boxes.length - 1}
                        className="h-6 w-6 p-0"
                      >
                        ▼
                      </Button>
                    </div>

                    {/* Color indicator */}
                    <div 
                      className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-bold text-lg"
                      style={{ backgroundColor: box.color || '#3B82F6' }}
                    >
                      {box.numero}
                    </div>

                    {/* Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-lg">{box.nombre}</h3>
                        {!box.activo && (
                          <Badge className="bg-slate-200 text-slate-600">Inactivo</Badge>
                        )}
                      </div>
                      {box.empresa_id && (
                        <p className="text-sm text-slate-500">
                          Empresa: {getEmpresaNombre(box.empresa_id)}
                        </p>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={box.activo}
                        onCheckedChange={() => handleToggleActive(box)}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenDialog(box)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeleteId(box.id)}
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        disabled={boxes.length <= 1}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Preview Card */}
        <Card>
          <CardHeader>
            <CardTitle>Vista Previa en Pantalla</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-slate-900 rounded-lg p-6">
              <div className="flex gap-4 flex-wrap justify-center">
                {boxes.filter(b => b.activo).map((box) => (
                  <div 
                    key={box.id}
                    className="rounded-xl p-4 text-center min-w-[150px]"
                    style={{ backgroundColor: `${box.color}20`, borderColor: box.color, borderWidth: 2 }}
                  >
                    <p className="text-white/70 text-sm mb-1">{box.nombre}</p>
                    <div 
                      className="text-4xl font-bold"
                      style={{ color: box.color }}
                    >
                      ---
                    </div>
                  </div>
                ))}
                {boxes.filter(b => b.activo).length === 0 && (
                  <p className="text-slate-500">No hay boxes activos para mostrar</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>
                {editingBox ? 'Editar Box' : 'Agregar Box'}
              </DialogTitle>
              <DialogDescription>
                Configure el punto de atención
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="nombre">Nombre del Box *</Label>
                  <Input
                    id="nombre"
                    value={formData.nombre}
                    onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                    placeholder="Ej: Box 1, Vacunación, Empresa ABC"
                    data-testid="input-nombre"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Color</Label>
                  <div className="flex gap-2 flex-wrap">
                    {COLORES_PRESET.map((color) => (
                      <button
                        key={color.value}
                        type="button"
                        onClick={() => setFormData({...formData, color: color.value})}
                        className={`w-10 h-10 rounded-lg transition-all ${
                          formData.color === color.value 
                            ? 'ring-2 ring-offset-2 ring-slate-400 scale-110' 
                            : 'hover:scale-105'
                        }`}
                        style={{ backgroundColor: color.value }}
                        title={color.label}
                      />
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Asignar a Empresa (opcional)</Label>
                  <Select 
                    value={formData.empresa_id || 'none'} 
                    onValueChange={(value) => setFormData({...formData, empresa_id: value === 'none' ? '' : value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sin empresa específica" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin empresa específica</SelectItem>
                      {empresas.map(empresa => (
                        <SelectItem key={empresa.id} value={empresa.id}>
                          {empresa.nombre}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">
                    Si asigna una empresa, este box será exclusivo para atender turnos de esa empresa.
                  </p>
                </div>

                <div className="flex items-center justify-between pt-2">
                  <div>
                    <Label>Estado</Label>
                    <p className="text-sm text-slate-500">
                      {formData.activo ? 'Activo - visible en pantalla' : 'Inactivo - oculto'}
                    </p>
                  </div>
                  <Switch
                    checked={formData.activo}
                    onCheckedChange={(checked) => setFormData({...formData, activo: checked})}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={handleCloseDialog}>
                  Cancelar
                </Button>
                <Button type="submit" className="gap-2" data-testid="save-btn">
                  <Save className="w-4 h-4" />
                  {editingBox ? 'Actualizar' : 'Guardar'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>¿Eliminar este box?</AlertDialogTitle>
              <AlertDialogDescription>
                Esta acción no se puede deshacer. El box será eliminado permanentemente.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700"
              >
                Eliminar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
};

export default BoxConfigPage;
